import type { BrandCard } from '../services/work';

/** How many brands picked last lead the list. */
export const RECENT_BRANDS = 3;

export type BrandRow =
  | { readonly kind: 'heading'; readonly label: string }
  | { readonly kind: 'brand'; readonly section: 'recent' | 'all' | 'found'; readonly brand: BrandCard; /** A date, only where another brand has the same name. */ readonly note?: string };

/** The ids remembered as picked last, newest first; anything unreadable is no history. */
export function readRecent(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const ids = JSON.parse(raw) as unknown;
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string').slice(0, RECENT_BRANDS) : [];
  } catch {
    return [];
  }
}

export function pushRecent(recent: readonly string[], id: string): string {
  return JSON.stringify([id, ...recent.filter((one) => one !== id)].slice(0, RECENT_BRANDS));
}

const folded = (text: string) => text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const byName = (a: BrandCard, b: BrandCard) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }) || (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');

/** "Edited 12 Oct" — or the year too when it is not this one. */
export function editedNote(updatedAt: string | null | undefined, now = new Date(), withTime = false): string | undefined {
  if (!updatedAt) return undefined;
  const at = new Date(updatedAt);
  if (Number.isNaN(at.getTime())) return undefined;
  const sameYear = at.getFullYear() === now.getFullYear();
  const day = at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
  return `Edited ${day}${withTime ? `, ${at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}`;
}

/**
 * The picker's rows. With no search: Recent (the ones picked last that still exist), then
 * All Brands A to Z without them. With a search: every brand whose name has each word, the
 * ones whose name starts with the search first. A brand sharing its name with another carries
 * when it was last edited, so "Crumb & Co copy" twice is two different things to read.
 */
export function brandRows(brands: readonly BrandCard[], recent: readonly string[], query: string, now = new Date()): BrandRow[] {
  const counts = new Map<string, number>();
  for (const brand of brands) counts.set(folded(brand.name), (counts.get(folded(brand.name)) ?? 0) + 1);
  // Twins edited on the same day are told apart by the time as well.
  const days = new Map<string, number>();
  for (const brand of brands) {
    const key = `${folded(brand.name)}|${editedNote(brand.updatedAt, now) ?? ''}`;
    days.set(key, (days.get(key) ?? 0) + 1);
  }
  const note = (brand: BrandCard) => {
    if ((counts.get(folded(brand.name)) ?? 0) < 2) return undefined;
    const sameDay = (days.get(`${folded(brand.name)}|${editedNote(brand.updatedAt, now) ?? ''}`) ?? 0) > 1;
    return editedNote(brand.updatedAt, now, sameDay) ?? 'Same name';
  };
  const row = (section: 'recent' | 'all' | 'found') => (brand: BrandCard): BrandRow => ({ kind: 'brand', section, brand, note: note(brand) });

  const words = folded(query).split(/\s+/).filter(Boolean);
  if (words.length > 0) {
    const whole = words.join(' ');
    const found = brands.filter((brand) => words.every((word) => folded(brand.name).includes(word)));
    const starts = (brand: BrandCard) => (folded(brand.name).startsWith(whole) ? 0 : 1);
    return [...found].sort((a, b) => starts(a) - starts(b) || byName(a, b)).map(row('found'));
  }

  const byId = new Map(brands.map((brand) => [brand.id, brand]));
  const lead = recent.map((id) => byId.get(id)).filter((brand): brand is BrandCard => !!brand);
  const rest = brands.filter((brand) => !lead.includes(brand)).sort(byName);
  // A short list is read at a glance: no headings, and nothing moved to the top.
  if (brands.length <= 8 || lead.length === 0) return [...brands].sort(byName).map(row('all'));
  return [{ kind: 'heading', label: 'Recent' }, ...lead.map(row('recent')), { kind: 'heading', label: 'All Brands' }, ...rest.map(row('all'))];
}
